import {
  CallTranscription,
  CallTranscriptionResponse,
  ListCallTranscriptionQueryParams
} from "src/models/callTranscriptions";
import { erebus } from "boot/axios";

export default class TranscriptionService {
  /**
   * Lists call transcriptions based on provided query parameters
   * @param params - Query parameters to filter transcriptions
   * @returns Promise resolving to array of call transcriptions
   */
  async listTranscriptions(params: ListCallTranscriptionQueryParams): Promise<CallTranscription[]> {
    this.validateParams(params);

    const res = await erebus.get("/transcripts", {
      params: {
        ...params
      }
    });

    // Convert the response to the CallTranscription type
    return res.data.map((transcription: CallTranscriptionResponse) => ({
      ID: transcription.ID,
      callRecordingID: transcription.callRecordingID,
      clientID: transcription.clientID,
      locationID: transcription.locationID,
      text: transcription.text,
      status: transcription.status,
      error: transcription.error || null,
      createdAt: new Date(transcription.createdAt),
      updatedAt: new Date(transcription.updatedAt)
    }));
  }

  /**
   * Retrieves a specific transcription by ID
   * @param ID - The UUID of the transcription to retrieve
   * @returns Promise resolving to a call transcription
   */
  async getTranscriptionByID(ID: string): Promise<CallTranscription> {
    const params = { ID };
    const transcripts = await this.listTranscriptions(params);
    if (transcripts.length === 0) {
      throw new Error(`Transcription with ID ${ID} not found.`);
    }
    if (transcripts.length > 1) {
      throw new Error(`Multiple transcriptions found with ID ${ID}.`);
    }
    return transcripts[0];
  }

  /**
   * Retrieves transcriptions for a specific case
   * @param caseID - The ID of the case
   * @returns Promise resolving to array of call transcriptions
   */
  async getTranscriptionsByCaseID(caseID: number): Promise<CallTranscription[]> {
    const params = { caseID };
    return await this.listTranscriptions(params);
  }

  /**
   * Retrieves transcriptions for a specific activation
   * @param activationID - The ID of the activation
   * @returns Promise resolving to array of call transcriptions
   */
  async getTranscriptionsByActivationID(activationID: number): Promise<CallTranscription[]> {
    const params = { activationID };
    return await this.listTranscriptions(params);
  }

  private validateParams(params: ListCallTranscriptionQueryParams): void {
    if (!params.activationID && !params.caseID && !params.ID) {
      throw new Error("At least one of activationID, caseID, or ID must be provided.");
    }
  }
}